import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import {
  PlusIcon,
  UsersIcon,
  UserPlusIcon,
  EyeIcon,
  TrashIcon,
  CurrencyDollarIcon,
  UserCircleIcon,
  BellIcon,
} from '@heroicons/react/24/outline';
import { membersAPI, batchesAPI, depositsAPI, notificationsAPI } from '../services/api';
import {
  StatCard,
  Badge,
  Modal,
  DataTable,
  Pagination,
  LoadingSpinner,
  EmptyState,
  SearchInput,
  FilterButton,
  ConfirmDialog,
} from '../components/common';
import { formatCurrency, formatDate, formatPhone } from '../utils/formatters';
import { toast } from 'react-hot-toast';

export default function MembersPage() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const batchFilter = searchParams.get('batch');

  // Separate form instances for each modal
  const { 
    register: registerRegister, 
    handleSubmit: handleRegisterSubmit, 
    reset: resetRegister, 
    formState: { errors: registerErrors } 
  } = useForm();
  
  const { 
    register: registerEnroll, 
    handleSubmit: handleEnrollSubmit, 
    reset: resetEnroll, 
    formState: { errors: enrollErrors } 
  } = useForm();
  
  const { 
    register: registerDeposit, 
    handleSubmit: handleDepositSubmit, 
    reset: resetDeposit, 
    formState: { errors: depositErrors } 
  } = useForm();
  
  const { 
    register: registerNotify, 
    handleSubmit: handleNotifySubmit, 
    reset: resetNotify, 
    formState: { errors: notifyErrors } 
  } = useForm();

  const [isRegisterModalOpen, setIsRegisterModalOpen] = useState(false);
  const [isEnrollModalOpen, setIsEnrollModalOpen] = useState(false);
  const [isManualDepositModalOpen, setIsManualDepositModalOpen] = useState(false);
  const [isNotifyModalOpen, setIsNotifyModalOpen] = useState(false);
  const [selectedMember, setSelectedMember] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedBatch, setSelectedBatch] = useState(batchFilter || 'all');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);

  // Fetch members
  const { data: membersData, isLoading } = useQuery({
    queryKey: ['members', selectedBatch, searchTerm, currentPage, pageSize],
    queryFn: async () => {
      const params = {
        page: currentPage,
        limit: pageSize,
      };
      
      if (selectedBatch && selectedBatch !== 'all') {
        params.batch_id = selectedBatch;
      }
      
      if (searchTerm) {
        params.search = searchTerm;
      }
      
      const response = await membersAPI.getAll(params);
      return response.data;
    },
  });

  const members = membersData?.data || [];
  const pagination = membersData?.pagination || { total: 0, pages: 1 };

  // Fetch batches
  const { data: batches } = useQuery({
    queryKey: ['batches-for-filter'],
    queryFn: async () => {
      const response = await batchesAPI.getAll();
      return Array.isArray(response.data) ? response.data : (response.data?.data || []);
    },
  });

  const registerMutation = useMutation({
    mutationFn: (data) => membersAPI.register(data),
    onSuccess: () => {
      queryClient.invalidateQueries(['members']);
      toast.success('Member registered successfully!');
      setIsRegisterModalOpen(false);
      resetRegister();
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || 'Failed to register member');
    },
  });

  const enrollMutation = useMutation({
    mutationFn: (data) => membersAPI.enroll(data),
    onSuccess: () => {
      queryClient.invalidateQueries(['members']);
      toast.success('Member enrolled successfully!');
      setIsEnrollModalOpen(false);
      setSelectedMember(null);
      resetEnroll();
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || 'Failed to enroll member');
    },
  });

  const manualDepositMutation = useMutation({
    mutationFn: (data) => depositsAPI.manualDeposit(data),
    onSuccess: () => {
      queryClient.invalidateQueries(['members']);
      toast.success('Manual deposit recorded successfully!');
      setIsManualDepositModalOpen(false);
      setSelectedMember(null);
      resetDeposit();
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || 'Failed to record deposit');
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (userId) => membersAPI.deleteMember(userId),
    onSuccess: () => {
      queryClient.invalidateQueries(['members']);
      toast.success('Member deleted successfully!');
      setConfirmDelete(null);
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || 'Failed to delete member');
    },
  });

  const notifyMutation = useMutation({
    mutationFn: (data) => notificationsAPI.create(data),
    onSuccess: () => {
      toast.success('Notification sent successfully!');
      setIsNotifyModalOpen(false);
      setSelectedMember(null);
      resetNotify();
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || 'Failed to send notification');
    },
  });

  const onRegisterSubmit = (data) => {
    console.log(data);
    registerMutation.mutate({
      phone: data.phone,
      language: data.language || 'en',
      role: 'member', // Default to member role
    });
  };

  const onEnrollSubmit = (data) => {
    console.log(data);
    enrollMutation.mutate({
      user_id: selectedMember.user_id,
      batch_id: parseInt(data.batch_id, 10),
      is_fixed_pool: false, // Default to regular pool
    });
  };

  const onManualDepositSubmit = (data) => {
    console.log(data);
    manualDepositMutation.mutate({
      user_id: selectedMember.user_id,
      amount_cents: parseInt(data.amount_cents, 10),
      notes: data.notes || '',
    });
  };

  const onNotifySubmit = (data) => {
    notifyMutation.mutate({
      user_id: selectedMember.user_id,
      title: data.title,
      body: data.body,
      notification_type: data.notification_type,
      audience: 'user',
    });
  };

  const handleEnroll = (member) => {
    setSelectedMember(member);
    setIsEnrollModalOpen(true);
  };

  const handleManualDeposit = (member) => {
    setSelectedMember(member);
    setIsManualDepositModalOpen(true);
  };

  const handleNotify = (member) => {
    setSelectedMember(member);
    setIsNotifyModalOpen(true);
  };

  const handleDelete = (member) => {
    setConfirmDelete(member);
  };

  const handleView = (member) => {
    navigate(`/members/${member.user_id}`);
  };

  const columns = [
    {
      key: 'phone',
      label: 'Phone',
      render: (value) => formatPhone(value),
    },
    {
      key: 'batch_name',
      label: 'Batch',
      render: (value, row) => value ? (
        <span className="text-sm">{value}</span>
      ) : (
        <Badge variant="default">Not Enrolled</Badge>
      ),
    },
    {
      key: 'deposit_cents',
      label: 'Deposit',
      render: (value) => formatCurrency(value || 0),
    },
    {
      key: 'payment_status',
      label: 'Status',
      render: (value) => value ? (
        <Badge variant={
          value === 'green' ? 'success' :
          value === 'yellow' ? 'warning' :
          'danger'
        }>
          {value}
        </Badge>
      ) : (
        <span className="text-gray-400">-</span>
      ),
    },
    {
      key: 'actions',
      label: 'Actions',
      render: (value, row) => (
        <div className="flex items-center gap-2">
          <button
            onClick={() => handleView(row)}
            className="p-1 text-blue-600 hover:text-blue-800"
            title="View Details"
          >
            <EyeIcon className="h-4 w-4" />
          </button>
          {!row.batch_id && (
            <button
              onClick={() => handleEnroll(row)}
              className="p-1 text-green-600 hover:text-green-800"
              title="Enroll in Batch"
            >
              <UserCircleIcon className="h-4 w-4" />
            </button>
          )}
          {row.batch_id && (
            <button
              onClick={() => handleManualDeposit(row)}
              className="p-1 text-purple-600 hover:text-purple-800"
              title="Manual Deposit"
            >
              <CurrencyDollarIcon className="h-4 w-4" />
            </button>
          )}
          <button
            onClick={() => handleNotify(row)}
            className="p-1 text-yellow-600 hover:text-yellow-800"
            title="Send Notification"
          >
            <BellIcon className="h-4 w-4" />
          </button>
          <button
            onClick={() => handleDelete(row)}
            className="p-1 text-red-600 hover:text-red-800"
            title="Delete Member"
          >
            <TrashIcon className="h-4 w-4" />
          </button>
        </div>
      ),
    },
  ];

  if (isLoading) {
    return <LoadingSpinner text="Loading members..." />;
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Members</h1>
          <p className="mt-1 text-sm text-gray-600">
            Manage member registrations and enrollments
          </p>
        </div>
        <button
          onClick={() => setIsRegisterModalOpen(true)}
          className="btn-primary flex items-center gap-2"
        >
          <PlusIcon className="h-5 w-5" />
          Register Member
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard
          title="Total Members"
          value={pagination.total || 0}
          icon={UsersIcon}
          color="primary"
        />
        <StatCard
          title="Green Status"
          value={members?.filter(m => m.payment_status === 'green').length || 0}
          icon={UsersIcon}
          color="green"
        />
        <StatCard
          title="Yellow Status"
          value={members?.filter(m => m.payment_status === 'yellow').length || 0}
          icon={UsersIcon}
          color="yellow"
        />
        <StatCard
          title="Not Enrolled"
          value={members?.filter(m => !m.batch_id).length || 0}
          icon={UsersIcon}
          color="gray"
        />
      </div>

      {/* Search and Filters */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <SearchInput
          value={searchTerm}
          onChange={setSearchTerm}
          placeholder="Search by phone..."
        />

        <div className="flex flex-wrap gap-2">
          <FilterButton
            active={selectedBatch === 'all'}
            onClick={() => setSelectedBatch('all')}
          >
            All Batches
          </FilterButton>
          {batches?.slice(0, 5).map((batch) => (
            <FilterButton
              key={batch.id}
              active={selectedBatch === batch.id.toString()}
              onClick={() => setSelectedBatch(batch.id.toString())}
            >
              {batch.name || `Batch ${batch.batch_number}`}
            </FilterButton>
          ))}
        </div>
      </div>

      {/* Members Table */}
      <div className="card">
        {members && members.length > 0 ? (
          <>
            <DataTable
              columns={columns}
              data={members}
            />
            <Pagination
              currentPage={currentPage}
              totalPages={pagination.pages}
              totalItems={pagination.total}
              itemsPerPage={pageSize}
              onPageChange={setCurrentPage}
              onPageSizeChange={setPageSize}
              isLoading={isLoading}
            />
          </>
        ) : (
          <EmptyState
            icon={UsersIcon}
            title="No members found"
            description={searchTerm || selectedBatch !== 'all'
              ? "Try adjusting your filters"
              : "Register and enroll your first member"
            }
            action={() => setIsRegisterModalOpen(true)}
            actionLabel="Register Member"
          />
        )}
      </div>

      {/* Register Modal */}
      <Modal
        isOpen={isRegisterModalOpen}
        onClose={() => {
          setIsRegisterModalOpen(false);
          resetRegister();
        }}
        title="Register New Member"
      >
        <form onSubmit={handleRegisterSubmit(onRegisterSubmit)} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Phone Number *
            </label>
            <input
              type="tel"
              {...registerRegister('phone', { required: 'Phone is required' })}
              className="input-field"
              placeholder="911234567"
            />
            {registerErrors.phone && (
              <p className="mt-1 text-sm text-red-600">{registerErrors.phone.message}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Language
            </label>
            <select {...registerRegister('language')} className="input-field">
              <option value="en">English</option>
              <option value="am">Amharic</option>
            </select>
          </div>

          <div className="flex justify-end gap-3 pt-4">
            <button
              type="button"
              onClick={() => {
                setIsRegisterModalOpen(false);
                resetRegister();
              }}
              className="btn-secondary"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={registerMutation.isPending}
              className="btn-primary"
            >
              {registerMutation.isPending ? 'Registering...' : 'Register'}
            </button>
          </div>
        </form>
      </Modal>

      {/* Enroll Modal */}
      <Modal
        isOpen={isEnrollModalOpen}
        onClose={() => {
          setIsEnrollModalOpen(false);
          setSelectedMember(null);
          resetEnroll();
        }}
        title={`Enroll ${selectedMember ? formatPhone(selectedMember.phone) : 'Member'}`}
      >
        <form onSubmit={handleEnrollSubmit(onEnrollSubmit)} className="space-y-4">
          <div className="p-3 bg-blue-50 rounded-lg mb-4">
            <p className="text-sm text-blue-800">
              <strong>Member:</strong> {selectedMember && formatPhone(selectedMember.phone)}
            </p>
            <p className="text-xs text-blue-600 mt-1">
              Will be enrolled in regular pool by default
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Select Batch *
            </label>
            <select
              {...registerEnroll('batch_id', { required: 'Batch is required' })}
              className="input-field"
            >
              <option value="">Select batch...</option>
              {batches?.filter(b => b.status === 'active' || b.status === 'open').map((batch) => (
                <option key={batch.id} value={batch.id}>
                  {batch.name || `Batch ${batch.batch_number}`}
                </option>
              ))}
            </select>
            {enrollErrors.batch_id && (
              <p className="mt-1 text-sm text-red-600">{enrollErrors.batch_id.message}</p>
            )}
          </div>

          <div className="flex justify-end gap-3 pt-4">
            <button
              type="button"
              onClick={() => {
                setIsEnrollModalOpen(false);
                setSelectedMember(null);
                resetEnroll();
              }}
              className="btn-secondary"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={enrollMutation.isPending}
              className="btn-primary"
            >
              {enrollMutation.isPending ? 'Enrolling...' : 'Enroll'}
            </button>
          </div>
        </form>
      </Modal>

      {/* Manual Deposit Modal */}
      <Modal
        isOpen={isManualDepositModalOpen}
        onClose={() => {
          setIsManualDepositModalOpen(false);
          setSelectedMember(null);
          resetDeposit();
        }}
        title={`Manual Deposit for ${selectedMember ? formatPhone(selectedMember.phone) : 'Member'}`}
      >
        <form onSubmit={handleDepositSubmit(onManualDepositSubmit)} className="space-y-4">
          <div className="p-3 bg-purple-50 rounded-lg mb-4">
            <p className="text-sm text-purple-800">
              <strong>Member:</strong> {selectedMember && formatPhone(selectedMember.phone)}
            </p>
            <p className="text-sm text-purple-800">
              <strong>Batch:</strong> {selectedMember?.batch_name || 'N/A'}
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Amount (in cents) *
            </label>
            <input
              type="number"
              {...registerDeposit('amount_cents', { 
                required: 'Amount is required',
                min: { value: 1, message: 'Amount must be greater than 0' }
              })}
              className="input-field"
              placeholder="10000"
            />
            {depositErrors.amount_cents && (
              <p className="mt-1 text-sm text-red-600">{depositErrors.amount_cents.message}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Notes (Optional)
            </label>
            <textarea
              {...registerDeposit('notes')}
              className="input-field"
              rows="3"
              placeholder="Add any notes about this deposit..."
            />
          </div>

          <div className="flex justify-end gap-3 pt-4">
            <button
              type="button"
              onClick={() => {
                setIsManualDepositModalOpen(false);
                setSelectedMember(null);
                resetDeposit();
              }}
              className="btn-secondary"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={manualDepositMutation.isPending}
              className="btn-primary"
            >
              {manualDepositMutation.isPending ? 'Recording...' : 'Record Deposit'}
            </button>
          </div>
        </form>
      </Modal>

      {/* Notify Modal */}
      <Modal
        isOpen={isNotifyModalOpen}
        onClose={() => {
          setIsNotifyModalOpen(false);
          setSelectedMember(null);
          resetNotify();
        }}
        title={`Send Notification to ${selectedMember ? formatPhone(selectedMember.phone) : ''}`}
        size="md"
      >
        <form onSubmit={handleNotifySubmit(onNotifySubmit)} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Notification Type *
            </label>
            <select
              {...registerNotify('notification_type', { required: 'Type is required' })}
              className="input-field"
            >
              <option value="">Select type...</option>
              <option value="general">General</option>
              <option value="payment_reminder">Payment Reminder</option>
              <option value="draw_notification">Draw Notification</option>
              <option value="winner_notification">Winner Notification</option>
              <option value="system">System</option>
            </select>
            {notifyErrors.notification_type && (
              <p className="mt-1 text-sm text-red-600">{notifyErrors.notification_type.message}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Title *
            </label>
            <input
              type="text"
              {...registerNotify('title', { required: 'Title is required' })}
              className="input-field"
              placeholder="Notification title"
            />
            {notifyErrors.title && (
              <p className="mt-1 text-sm text-red-600">{notifyErrors.title.message}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Message *
            </label>
            <textarea
              {...registerNotify('body', { required: 'Message is required' })}
              className="input-field"
              rows="4"
              placeholder="Notification message..."
            />
            {notifyErrors.body && (
              <p className="mt-1 text-sm text-red-600">{notifyErrors.body.message}</p>
            )}
          </div>

          <div className="flex justify-end gap-3 pt-4">
            <button
              type="button"
              onClick={() => {
                setIsNotifyModalOpen(false);
                setSelectedMember(null);
                resetNotify();
              }}
              className="btn-secondary"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={notifyMutation.isPending}
              className="btn-primary"
            >
              {notifyMutation.isPending ? 'Sending...' : 'Send Notification'}
            </button>
          </div>
        </form>
      </Modal>

      {/* Delete Confirmation */}
      {confirmDelete && (
        <ConfirmDialog
          isOpen={true}
          onClose={() => setConfirmDelete(null)}
          onConfirm={() => deleteMutation.mutate(confirmDelete.user_id)}
          title="Delete Member"
          message={`Are you sure you want to delete ${formatPhone(confirmDelete.phone)}? This action cannot be undone.`}
          confirmLabel="Delete"
          variant="danger"
          isLoading={deleteMutation.isLoading}
        />
      )}
    </div>
  );
}
